import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

/**
 * Fetch admin dashboard KPIs — counts, revenue, chart data.
 */
export function useAdminStats() {
  return useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: async () => {
      const [products, orders, categories, testimonials, categoriesWithProducts] = await Promise.all([
        supabase.from('products').select('id', { count: 'exact', head: true }),
        supabase.from('orders').select('id, total_price, status, created_at, customer_name, product_snapshot, quantity', { count: 'exact' }),
        supabase.from('categories').select('id', { count: 'exact', head: true }),
        supabase.from('testimonials').select('id', { count: 'exact', head: true }),
        supabase.from('categories').select('name, products(id)').order('order', { ascending: true }),
      ]);

      const orderList = orders.data || [];

      const totalRevenue = orderList.reduce(
        (sum, o) => sum + (Number(o.total_price) || 0),
        0
      );

      // Orders by status
      const ordersByStatus = {};
      orderList.forEach((o) => {
        ordersByStatus[o.status] = (ordersByStatus[o.status] || 0) + 1;
      });

      // Revenue by status
      const revenueByStatus = {};
      orderList.forEach((o) => {
        revenueByStatus[o.status] = (revenueByStatus[o.status] || 0) + (Number(o.total_price) || 0);
      });

      // Orders per day (last 7 days)
      const now = new Date();
      const dailyOrders = [];
      const dailyRevenue = [];
      for (let i = 6; i >= 0; i--) {
        const day = new Date(now);
        day.setDate(day.getDate() - i);
        const dayStr = day.toISOString().slice(0, 10);
        const dayLabel = day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        const dayOrders = orderList.filter(
          (o) => o.created_at && o.created_at.slice(0, 10) === dayStr
        );
        dailyOrders.push({ date: dayStr, label: dayLabel, count: dayOrders.length });
        dailyRevenue.push({
          date: dayStr,
          label: dayLabel,
          revenue: dayOrders.reduce((s, o) => s + (Number(o.total_price) || 0), 0),
        });
      }

      // Products per category
      const productsByCategory = (categoriesWithProducts.data || []).map((c) => ({
        category: c.name,
        count: c.products?.length || 0,
      }));

      // Recent orders (latest 5)
      const recentOrders = [...orderList]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);

      // Today's stats
      const todayStr = now.toISOString().slice(0, 10);
      const todayOrders = orderList.filter(
        (o) => o.created_at && o.created_at.slice(0, 10) === todayStr
      );
      const todayRevenue = todayOrders.reduce(
        (s, o) => s + (Number(o.total_price) || 0),
        0
      );

      // Pending orders count
      const pendingCount = orderList.filter((o) => o.status === 'pending').length;

      return {
        productCount: products.count || 0,
        orderCount: orders.count || 0,
        categoryCount: categories.count || 0,
        testimonialCount: testimonials.count || 0,
        totalRevenue,
        ordersByStatus,
        revenueByStatus,
        dailyOrders,
        dailyRevenue,
        productsByCategory,
        recentOrders,
        todayOrderCount: todayOrders.length,
        todayRevenue,
        pendingCount,
      };
    },
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Fetch all products for admin management.
 */
export function useAdminProducts() {
  return useQuery({
    queryKey: ['admin', 'products'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*, categories(name)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Fetch all orders for admin management with filters.
 */
export function useAdminOrders({ status, search, dateFrom, dateTo } = {}) {
  return useQuery({
    queryKey: ['admin', 'orders', { status, search, dateFrom, dateTo }],
    queryFn: async () => {
      let query = supabase
        .from('orders')
        .select('*, products(title, image_path, price)')
        .order('created_at', { ascending: false });

      if (status && status !== 'all') {
        query = query.eq('status', status);
      }
      if (dateFrom) {
        query = query.gte('created_at', dateFrom);
      }
      if (dateTo) {
        query = query.lte('created_at', dateTo);
      }
      if (search) {
        query = query.or(
          `customer_name.ilike.%${search}%,customer_phone.ilike.%${search}%`
        );
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 1,
  });
}

/**
 * Fetch all categories for admin (ordered by sort order).
 */
export function useAdminCategories() {
  return useQuery({
    queryKey: ['admin', 'categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('order', { ascending: true });
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Fetch order history (status audit trail) for a single order.
 */
export function useOrderHistory(orderId) {
  return useQuery({
    queryKey: ['admin', 'order-history', orderId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('order_history')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!orderId,
    staleTime: 1000 * 60 * 1,
  });
}

/**
 * Fetch all testimonials for admin.
 */
export function useAdminTestimonials() {
  return useQuery({
    queryKey: ['admin', 'testimonials'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('testimonials')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

/* ══════════════════════════════════════════════
 * MUTATIONS
 * ══════════════════════════════════════════════ */

/**
 * Update an existing product.
 */
export function useUpdateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }) => {
      const { data, error } = await supabase
        .from('products')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

/**
 * Soft-delete a product (set deleted_at + is_active = false).
 */
export function useDeleteProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from('products')
        .update({ deleted_at: new Date().toISOString(), is_active: false })
        .eq('id', id);
      if (error) throw error;
      return { id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

/**
 * Bulk soft-delete products.
 */
export function useBulkDeleteProducts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids) => {
      const { error } = await supabase
        .from('products')
        .update({ deleted_at: new Date().toISOString(), is_active: false })
        .in('id', ids);
      if (error) throw error;
      return { ids };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

/**
 * Check if a product has pending orders.
 */
export async function checkPendingOrders(productId) {
  const { count } = await supabase
    .from('orders')
    .select('id', { count: 'exact', head: true })
    .eq('product_id', productId)
    .eq('status', 'pending');
  return count > 0;
}

/* ══════════════════════════════════════════════
 * CATEGORY MUTATIONS
 * ══════════════════════════════════════════════ */

/**
 * Create a new category.
 */
export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, slug, icon }) => {
      // Get max order value
      const { data: existing } = await supabase
        .from('categories')
        .select('order')
        .order('order', { ascending: false })
        .limit(1);
      const nextOrder = (existing?.[0]?.order ?? -1) + 1;

      const { data, error } = await supabase
        .from('categories')
        .insert({ name, slug, icon: icon || null, order: nextOrder })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

/**
 * Update an existing category.
 */
export function useUpdateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }) => {
      const { data, error } = await supabase
        .from('categories')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/**
 * Delete a category. Optionally reassign its products to another category.
 */
export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reassignTo = null }) => {
      // Reassign products if requested
      if (reassignTo) {
        const { error: moveErr } = await supabase
          .from('products')
          .update({ category_id: reassignTo })
          .eq('category_id', id);
        if (moveErr) throw moveErr;
      } else {
        // Set products' category to null
        const { error: nullErr } = await supabase
          .from('products')
          .update({ category_id: null })
          .eq('category_id', id);
        if (nullErr) throw nullErr;
      }
      const { error } = await supabase.from('categories').delete().eq('id', id);
      if (error) throw error;
      return { id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

/**
 * Reorder categories — update the "order" column for each.
 */
export function useReorderCategories() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orderedIds) => {
      // orderedIds is an array of category IDs in their new order
      const updates = orderedIds.map((id, index) =>
        supabase.from('categories').update({ order: index }).eq('id', id)
      );
      const results = await Promise.all(updates);
      const failed = results.find((r) => r.error);
      if (failed) throw failed.error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
}

/**
 * Check how many products belong to a category.
 */
export async function getCategoryProductCount(categoryId) {
  const { count } = await supabase
    .from('products')
    .select('id', { count: 'exact', head: true })
    .eq('category_id', categoryId)
    .eq('is_active', true);
  return count || 0;
}

/* ══════════════════════════════════════════════
 * ORDER MUTATIONS
 * ══════════════════════════════════════════════ */

/**
 * Update order status — also writes to order_history.
 */
export function useUpdateOrderStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ orderId, newStatus, note }) => {
      // Get current status
      const { data: current, error: fetchErr } = await supabase
        .from('orders')
        .select('status')
        .eq('id', orderId)
        .single();
      if (fetchErr) throw fetchErr;

      // Update order
      const { error: updateErr } = await supabase
        .from('orders')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', orderId);
      if (updateErr) throw updateErr;

      // Write history record
      const { data: { user } } = await supabase.auth.getUser();
      const { error: histErr } = await supabase.from('order_history').insert({
        order_id: orderId,
        old_status: current.status,
        new_status: newStatus,
        changed_by: user?.id || null,
        note: note || null,
      });
      if (histErr) throw histErr;

      return { orderId, newStatus };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'order-history', variables.orderId] });
    },
  });
}

/**
 * Add internal note to an order.
 */
export function useAddOrderNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ orderId, note }) => {
      const { error } = await supabase
        .from('orders')
        .update({
          internal_notes: note,
          updated_at: new Date().toISOString(),
        })
        .eq('id', orderId);
      if (error) throw error;
      return { orderId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
    },
  });
}

/**
 * Mark order owner as notified.
 */
export function useMarkNotified() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orderId) => {
      const { error } = await supabase
        .from('orders')
        .update({ notified_owner: true, updated_at: new Date().toISOString() })
        .eq('id', orderId);
      if (error) throw error;
      return { orderId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
    },
  });
}
